import React, { useCallback, useEffect, useRef, useState } from "react";
import useApi from "../api/api";
import { applyUi } from "../theme/ui";
import { ReactComponent as CardIcon } from "../assets/ico_card.svg";
import { ReactComponent as BankIcon } from "../assets/ico_bank.svg";
import { ReactComponent as PayPalIcon } from "../assets/ico_paypal.svg";
import { ReactComponent as VenmoIcon } from "../assets/ico_venmo.svg";

const methodIcon = (paymentType) => {
  switch ((paymentType || "").split(":")[0]) {
    case "pushtocard":
      return <CardIcon />;
    case "pushtoaccount":
      return <BankIcon />;
    case "venmo":
      return <VenmoIcon />;
    default:
      return <PayPalIcon />;
  }
};

const capitalize = (label) => label.charAt(0).toUpperCase() + label.slice(1);

const addedOn = (iso) =>
  iso
    ? `Added ${new Date(`${iso}Z`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
    : "";

/**
 * Payee Management POC: where an off-network recipient manages how they receive money.
 *
 * Sign-in is a one-time code sent to a phone or email we have already sent money to — there is
 * no password. Saved methods are listed per bank: a method saved while receiving from one bank is
 * only ever offered for that bank. New methods are added by ticking "Save this for next time" on
 * a claim, not here.
 */
// The session token is kept for this tab only (survives a reload, gone when the tab closes). It
// is sent as a header rather than a cookie: the app and API are different origins, and browsers
// that block cross-origin cookies (Safari) would otherwise lose the session.
const TOKEN_KEY = "np_payee_session";
const readToken = () => {
  try {
    return window.sessionStorage.getItem(TOKEN_KEY) || "";
  } catch (e) {
    return "";
  }
};
const writeToken = (token) => {
  try {
    if (token) {
      window.sessionStorage.setItem(TOKEN_KEY, token);
    } else {
      window.sessionStorage.removeItem(TOKEN_KEY);
    }
  } catch (e) {
    // storage unavailable — the session lasts until this page is left
  }
};

const PayeeProfile = () => {
  const api = useApi();
  const tokenRef = useRef(readToken());
  const auth = () => ({ headers: { Authorization: `Bearer ${tokenRef.current}` } });
  const [step, setStep] = useState("loading"); // loading | enter | code | profile
  const [idType, setIdType] = useState("phone");
  const [idValue, setIdValue] = useState("");
  const [code, setCode] = useState("");
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    applyUi(true);
  }, []);

  const loadProfile = useCallback(async () => {
    if (!tokenRef.current) {
      setProfile(null);
      setStep("enter");
      return;
    }
    try {
      const response = await api.get("/payee/profile", {
        headers: { Authorization: `Bearer ${tokenRef.current}` },
      });
      setProfile(response.data);
      setStep("profile");
    } catch (err) {
      tokenRef.current = "";
      writeToken("");
      setProfile(null);
      setStep("enter");
    }
  }, [api]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const startLogin = () =>
    run(async () => {
      if (!idValue.trim()) {
        throw new Error();
      }
      await api.post("/payee/auth/start", { type: idType, value: idValue });
      setStep("code");
    });

  const verifyLogin = () =>
    run(async () => {
      const response = await api.post("/payee/auth/verify", {
        type: idType,
        value: idValue,
        code,
      });
      tokenRef.current = response.data.token;
      writeToken(response.data.token);
      setCode("");
      await loadProfile();
    });

  const makeDefault = (uuid) =>
    run(async () => {
      await api.patch(`/payee/methods/${uuid}`, null, auth());
      await loadProfile();
    });

  const deleteMethod = (uuid) =>
    run(async () => {
      await api.delete(`/payee/methods/${uuid}`, auth());
      await loadProfile();
    });

  const setSms = (optedIn) =>
    run(async () => {
      await api.put("/payee/sms-consent", { opted_in: optedIn }, auth());
      await loadProfile();
    });

  const deleteProfile = () =>
    run(async () => {
      await api.delete("/payee/profile", auth());
      tokenRef.current = "";
      writeToken("");
      setProfile(null);
      setIdValue("");
      setStep("deleted");
    });

  const logout = () =>
    run(async () => {
      await api.post("/payee/auth/logout", null, auth());
      tokenRef.current = "";
      writeToken("");
      setProfile(null);
      setStep("enter");
    });

  const renderEnter = () => (
    <>
      <h5 className="np-h">Manage how you receive money</h5>
      <p className="np-h-sub">
        Enter the phone number or email people send you money at. We'll send you a sign-in code.
      </p>
      <div className="np-login-seg" role="radiogroup" aria-label="Sign in with">
        {["phone", "email"].map((t) => (
          <button
            key={t}
            type="button"
            role="radio"
            aria-checked={idType === t}
            className={`np-login-seg-option${idType === t ? " is-active" : ""}`}
            onClick={() => {
              setIdType(t);
              setIdValue("");
            }}
          >
            {t === "phone" ? "Phone" : "Email"}
          </button>
        ))}
      </div>
      <input
        id="payee-identifier"
        type={idType === "phone" ? "tel" : "email"}
        placeholder={idType === "phone" ? "(555) 123-4567" : "you@example.com"}
        value={idValue}
        onChange={(e) => setIdValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && startLogin()}
      />
      <button className="button_primary" onClick={startLogin} disabled={busy}>
        {busy ? <span className="button_spinner" aria-label="Loading" /> : "Send code"}
      </button>
    </>
  );

  const renderCode = () => (
    <>
      <h5 className="np-h">Enter your code</h5>
      <p className="np-h-sub">
        {idType === "phone"
          ? `If you've turned on texts for ${idValue}, a 6-digit code is on its way. It expires in 10 minutes.`
          : `If we've sent money to ${idValue}, a 6-digit code is on its way. It expires in 10 minutes.`}
      </p>
      <input
        id="payee-code"
        className="np-code-input"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        onKeyDown={(e) => e.key === "Enter" && verifyLogin()}
      />
      <button
        className="button_primary"
        onClick={verifyLogin}
        disabled={busy || code.length !== 6}
      >
        {busy ? <span className="button_spinner" aria-label="Loading" /> : "Sign in"}
      </button>
      <button onClick={() => setStep("enter")}>Use a different phone or email</button>
    </>
  );

  const renderProfile = () => {
    const phones = profile.contacts.filter((c) => c.type === "phone");
    const smsOn = phones.some((c) => c.sms_opted_in === true);
    return (
      <>
        <h5 className="np-h">How you receive money</h5>
        <p className="np-h-sub np-profile-signed-in">
          Signed in as {profile.contacts.map((c) => c.masked).join(", ")}
        </p>
        <div className="np-profile-intro">
          <p>
            These are the cards and accounts you've saved to receive money. When someone at one of
            these banks sends you money, we'll text or email you a link that asks before sending it
            to your default. You can always choose a different way instead.
          </p>
          <p>
            Each bank only sees what you saved while receiving from it. Removing a method here
            doesn't affect payments you've already received.
          </p>
        </div>

        {profile.institutions.length === 0 && (
          <div className="np-card np-card-pad np-profile-section">
            Nothing saved yet. Next time someone sends you money, tick "Save this for next time"
            and it will show up here.
          </div>
        )}

        {profile.institutions.map((fi) => (
          <div key={fi.uuid} className="np-card np-profile-section">
            <div className="np-profile-fi">
              {fi.logo_url && <img src={fi.logo_url} alt="" />}
              <span>From {fi.name}</span>
            </div>
            {fi.methods.length === 0 && (
              <div className="np-method-row">
                <span className="np-method-label">No saved methods</span>
              </div>
            )}
            {fi.methods.map((m) => (
              <div key={m.uuid} className="np-method-row">
                <span className="np-method-icon" aria-hidden="true">
                  {methodIcon(m.payment_type)}
                </span>
                <span className="np-method-body">
                  <span className="np-method-label">{capitalize(m.label)}</span>
                  <span className="np-method-sub">
                    {m.saving ? "Saving card… · " : ""}
                    {m.is_default ? "Default · " : ""}
                    {addedOn(m.created_at)}
                  </span>
                </span>
                <span className="np-method-actions">
                  {!m.is_default && (
                    <button onClick={() => makeDefault(m.uuid)} disabled={busy}>
                      Make default
                    </button>
                  )}
                  <button className="np-btn-quiet" onClick={() => deleteMethod(m.uuid)} disabled={busy}>
                    Remove
                  </button>
                </span>
              </div>
            ))}
          </div>
        ))}

        {phones.length > 0 && (
          <div className="np-card np-card-pad np-profile-section">
            <label className="np-check">
              <input
                type="checkbox"
                checked={smsOn}
                onChange={(e) => setSms(e.target.checked)}
                disabled={busy}
              />
              <span>
                Text me when someone sends me money. Msg &amp; data rates may apply. Reply STOP to
                opt out.
              </span>
            </label>
          </div>
        )}

        <button onClick={logout} disabled={busy}>
          Sign out
        </button>
        <button className="np-btn-danger" onClick={() => setStep("confirmDelete")} disabled={busy}>
          Delete my profile
        </button>
      </>
    );
  };

  const renderConfirmDelete = () => (
    <>
      <h5 className="np-h">Delete your profile?</h5>
      <p className="np-h-sub">
        We'll forget every saved way to receive money. Past payments aren't affected. Next time
        someone sends you money, you'll choose how to receive it from scratch.
      </p>
      <button className="np-btn-danger" onClick={deleteProfile} disabled={busy}>
        Delete profile
      </button>
      <button onClick={() => setStep("profile")}>Keep my profile</button>
    </>
  );

  const renderScreen = () => {
    switch (step) {
      case "enter":
        return renderEnter();
      case "code":
        return renderCode();
      case "profile":
        return renderProfile();
      case "confirmDelete":
        return renderConfirmDelete();
      case "deleted":
        return (
          <>
            <h5 className="np-h">Profile deleted</h5>
            <p className="np-h-sub">We no longer have any saved way for you to receive money.</p>
          </>
        );
      default:
        return <span className="button_spinner" aria-label="Loading" />;
    }
  };

  return (
    <main>
      <nav aria-label="Header" />
      <div className="np-screen">
        {error && <div className="np-error">{error}</div>}
        {renderScreen()}
      </div>
    </main>
  );
};

export default PayeeProfile;
